import { CalendarViewWeek } from "@mui/icons-material";
import { BasicFormikForm } from "../form/BasicFormikForm";
import BasicModal from "../helpers/BasicModal";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { array, boolean, object, setLocale, string } from "yup";
import { en } from "yup-locales";
import { _ACTIONS, _HTTP, _MESSAGES, _SWAL_ICONS } from "../../utils/constants";
import { _ROUTES } from "../../utils/_routes_constants";
import { createToast } from "../../utils/toast";
import { DataService } from "../../services/DataService";
import { buildOptions } from "../../utils/formHelper";
import { IndicatorInlineList } from "./IndicatorInlineList";

export const TemplateModal = ({
  showModal = false,
  setShowModal = () => {},
  editData = null,
  onDataMutation = () => {},
}) => {
  const [validationSchema, setValidationSchema] = useState(null);
  const dataService = new DataService();
  const [options, setOptions] = useState({
    activity: [],
    sites: [],
  });

  setLocale(en);

  const formik = useFormik({
    initialValues: {
      name: "",
      activity: "",
      sites: [],
      active: true,
      indicators: [],
    },
    onSubmit: (values) => {
      const method = editData ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url =
        _ROUTES.INDICATORS_TEMPLATES + (editData ? `/${editData.id}` : "");
      const template = {
        ...values,
      };

      if (template.activity === "") {
        template.activity = null;
      }

      if (template.sites && template.sites.length > 0) {
        template.sites = template.sites.map((el) => el.value);
      }

      if (template.indicators && template.indicators.length > 0) {
        /**
         * For indicators who have "@id" property, just send it without any other keys,
         * to avoid creating new indicators.
         *
         */
        template.indicators = template.indicators.map((el) => {
          if (el["@id"]) {
            return el["@id"];
          }

          if (editData && editData["@id"]) {
            el.template = editData["@id"];
          }

          return el;
        });
      }

      try {
        dataService.doRequest(method, url, template, {}, async (res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            const responseData = await res.json();
            onDataMutation(editData, responseData);
            setShowModal(false);
            createToast(_SWAL_ICONS.success, "Template saved successfully");
          }
        });
      } catch (error) {
        createToast(_SWAL_ICONS.error, _MESSAGES.error);
      }
    },
    validationSchema: validationSchema,
  });

  // Generate the validation schema based on current values
  const updateValidationSchema = () => {
    const validationSchemaObject = {
      name: string().required().label("Name"),
      activity: string().label("Activity"),
      sites: array().min(1).label("Sites"),
      active: boolean().label("Active"),
      indicators: array().label("Indicators"),
    };

    setValidationSchema(object(validationSchemaObject));
  };

  const getCustomStructure = () => {
    const structure = {
      0: ["name"],
      1: ["activity", "sites"],
      2: ["active"],
      3: ["indicators"],
    };

    return structure;
  };

  formik.special = {
    placeholder: {
      activity: "Select an activity",
      sites: "Select a site",
    },
    helperText: {
      activity: "One or null for all",
      sites: "Select one or more sites",
    },
    customStructure: getCustomStructure(),
    customComponents: {
      indicators: (
        <IndicatorInlineList
          value={formik.values.indicators}
          onChange={(value) => formik.setFieldValue("indicators", value)}
        />
      ),
    },
    autocomplete: {
      activity: {
        optionLabel: "name",
        optionValue: "id",
        multiple: false,
      },
      sites: {
        optionLabel: "name",
        optionValue: "id",
        multiple: true,
      },
    },
    canBeCleaned: {
      activity: true,
    },
  };

  useEffect(() => {
    if (editData) {
      const values = {
        name: editData.name,
        activity: editData.activity ? editData.activity["@id"] : "",
        sites: editData.sites
          ? editData.sites.map((el) => {
              return {
                value: el["@id"],
                label: el.name,
              };
            })
          : [],
        active: editData.active,
        indicators: editData.indicators,
      };

      formik.setValues(values);
    } else {
      formik.resetForm();
    }
  }, [editData]);

  useEffect(() => {
    // Initial setup of validation schema and custom structure
    updateValidationSchema();

    const optionsData = [];

    if (options.activity.length === 0) {
      dataService.fetchData(
        _ROUTES.ACTIVITIES,
        {
          pagination: false,
          active: 1,
          "order[name]": "ASC",
          "groups[]": "select:get",
        },
        (data) => {
          optionsData["activity"] = buildOptions(data, "name", "@id");
        },
      );
    }

    if (options.sites.length === 0) {
      dataService.fetchData(
        _ROUTES.SITES,
        {
          pagination: false,
          active: 1,
          "order[name]": "ASC",
          "groups[]": "select:get",
        },
        (data) => {
          optionsData["sites"] = buildOptions(data, "name", "@id");
        },
      );
    }

    setOptions(optionsData);
  }, []);

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={setShowModal}
      title={"Template"}
      content={
        <BasicFormikForm
          formik={formik}
          setShowModal={setShowModal}
          validations={validationSchema}
          customStructure={getCustomStructure()}
          options={options}
        />
      }
      icon={<CalendarViewWeek />}
      width={"900"}
    />
  );
};
